import React from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getErrorMessage,
  getPageName,
} from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { Content, ReleaseResponse } from '../../Release/types';
import InsertForm from '../components/InsertForm';
import { ReleasePayload } from '../types';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import i18n from '../../../i18n';
import { useRelease } from '../../../apis/release/ReleaseProvider';
import NavBar, { NavBarItem } from '../../../components/NavBar';
import { downloadContent } from '../../../services/contentService';

const ToReleaseCreateView: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');
  const { cadastraRelease, setAlert } = useRelease();

  const selectedItems: ReleaseResponse[] = location.state?.selectedRows;
  const rowHeight = 60;

  React.useEffect(() => {
    if (!selectedItems) {
      navigate('/');
    }
  }, [selectedItems, navigate]);

  const navItems: NavBarItem[] = [
    { label: t('release.labels.home'), route: '/', iconName: 'house-door' },
    {
      label: t('release.labels.list'),
      route: '/release',
      iconName: 'table',
    },
  ];
  
  const handleDownload = async (content: Content) => {
    try {
      const response = await downloadContent(content.id);
      
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${content.description}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: t('release.fields.id'), width: 80 },
    { field: 'fromIdentification', headerName: t('release.fields.from'), width: 80 },
    { field: 'clientName', headerName: t('release.fields.client'), width: 150 },
    { field: 'supplierName', headerName: t('release.fields.supplier'), width: 240 },
    { field: 'wrNumber', headerName: t('release.fields.wrNumber'), width: 130 },
    { field: 'poNumber', headerName: t('release.fields.poNumber'), width: 130 },
    {
      field: 'piecesWeight',
      headerName: t('release.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight } = params.row;
        return `${pieces} / ${weight}`;
      },
    },
    { field: 'dawb', headerName: t('release.fields.dawb'), width: 150 },
    {
      field: 'invoice',
      headerName: t('release.fields.invoiceNumber'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'pu',
      headerName: t('release.fields.pu'),
      type: 'date',
      width: 150,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      },
    },
    {
      field: 'input',
      headerName: t('release.fields.input'),
      type: 'date',
      width: 150,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      },
    },
    {
      field: 'content',
      headerName: t('release.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => {
        const cargoContents = params.row?.cargoContents;

        if (!cargoContents || cargoContents.length === 0) {
          return <span>—</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {cargoContents.map((cargoContent: any, index: number) => {
              const content = cargoContent?.content;
              if (!content || !content.description) return null;

              const contentWithId = { ...content, id: cargoContent.contentId };

              return (
                <React.Fragment key={index}>
                  {index > 0 && <span style={{ color: '#999', fontSize: '12px' }}>|</span>}
                  <span
                    onClick={() => handleDownload(contentWithId)}
                    style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '12px', whiteSpace: 'nowrap' }}
                    title={t('release.labels.clickToDownload')}
                  >
                    {content.description}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        );
      }
    }
  ];


  const handleAddRelease = async (data: ReleasePayload, files: File[]) => {
    try {
      const formData = new FormData();
      formData.append('shippedBy', data.shippedBy);
      formData.append('destinationId', data.destination);
      formData.append('observation', data.obs);
      formData.append('freightTypeId', data.freightType);
      formData.append('consigneeId', data.consignee);
      formData.append('tc', '4');
      formData.append('toId', data.to);
      files.forEach((file) => {
        formData.append('content', file);
      });
      selectedItems.forEach((cargo) => {
        formData.append('cargoIdList', cargo.id.toString());
      });

      await cadastraRelease(formData);
      navigate('/release');
    } catch (error: any) {
      // Usa a mensagem do erro se disponível (ex: "Permissão Bloqueada")
      const errorMessage = error?.message || getErrorMessage('registerReleaseError');
      setAlert({
        message: errorMessage,
        variant: 'danger',
      });
    }
  };

  return (
    <Container fluid>
      <NavBar items={navItems} />
      <div className="mb-3"></div>
      <h2>{getPageName('to-release')}</h2>
      <InsertForm onSubmitForm={handleAddRelease} />
      <div style={{ height: 300, width: '100%' }}>
        <DataGrid
          rows={selectedItems}
          columns={columns}
          pageSizeOptions={[5, 10, 20, 100]}
          pagination
          disableRowSelectionOnClick

          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              paddingTop: '8px',
              paddingBottom: '8px',
            },
          }}
        />
      </div>
    </Container>
  );
};

export default ToReleaseCreateView;
